import Header from "../../layouts/Header";
import AutoStackCarousel from "../../components/AutoStackCarousel";
import AutoStackPerformanceChart from "../../components/AutoStackPerformanceChart";
import { Typography } from "../../components/ui/typography";
import StackCard from "../../components/StackCard";
import { Button } from "../../components/ui";
import { PlusIcon } from "lucide-react";
import { Skeleton } from "../../components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  useAutoStackStats,
  useAutoStackTemplates,
  useQueryDigitalWallet,
  useUserAutoStacks,
} from "../../hooks/query/queries";
import { updateUserAutoStack } from "../../services/autostackService";
import { useQueryClient } from "@tanstack/react-query";
import CurrencyFormatter from "../../services/currencyFormatterService";
import {
  AutoStackingTemplateResponse,
  UserAutoStackResponse,
} from "../../types/generated/api";
import { Asset } from "../../types/asset";
import AutoStackPerformanceChartSkeleton from "../../components/skeletons/AutoStackPerformanceChartSkeleton";
import MyStacksSkeleton from "../../components/skeletons/MyStacksSkeleton";

export default function AutostackDashboard() {
  const navigate = useNavigate();

  const {
    data: autoStackStats,
    isLoading: isLoadingAutoStackStats,
    isFetching: isFetchingAutoStackStats,
    error,
  } = useAutoStackStats();

  const {
    data: autoStackTemplates,
    isLoading: isLoadingTemplates,
    isFetching: isFetchingTemplates,
  } = useAutoStackTemplates();
  const {
    data: userAutoStacks,
    isLoading: isLoadingUserStacks,
    isFetching: isFetchingUserStacks,
    error: errorUserStacks,
  } = useUserAutoStacks();

  const {
    digitalWalletData,
    isLoading: isLoadingDigitalWallet,
    isFetching: isFetchingDigitalWallet,
  } = useQueryDigitalWallet();

  const queryClient = useQueryClient();
  const [updatingStackId, setUpdatingStackId] = useState<string | null>(null);

  const currencyFormatter = new CurrencyFormatter({ locale: "en-ZA" });

  const fiatWalletBalance =
    digitalWalletData?.balances?.assetBalances?.fiat?.[0]?.availableAmount || 0;
  interface StackData {
    id: string;
    icon: string;
    assetName: string;
    title: string;
    amount: number;
    amountText: string;
    frequency: string;
    estimatedTokens: string;
    estimatedValue: string;
    isUserStack?: boolean;
    nextPurchase?: string;
    stackData?: UserAutoStackResponse;
  }

  const formatTotalStacked = () => {
    if (
      isLoadingAutoStackStats ||
      isFetchingAutoStackStats ||
      isLoadingDigitalWallet ||
      isFetchingDigitalWallet
    ) {
      return <Skeleton className="h-10 w-32 bg-white/20" />;
    }
    if (error || !autoStackStats?.totalStacked) return "R 0.00";

    return currencyFormatter.format(autoStackStats.totalStacked, "fiat", "ZAR");
  };

  const handleToggleStackStatus = async (
    stackId: string,
    currentStatus: string
  ) => {
    const newStatus = currentStatus === "active" ? "paused" : "active";
    setUpdatingStackId(stackId);

    queryClient.setQueryData(["userAutoStacks"], (oldData: any) => {
      if (!oldData?.results) return oldData;

      return {
        ...oldData,
        results: oldData.results.map((stack: any) =>
          stack._id === stackId ? { ...stack, status: newStatus } : stack
        ),
      };
    });

    try {
      await updateUserAutoStack(stackId, {
        status: newStatus as "active" | "paused",
      });
    } catch (error) {
      console.error("Failed to update stack status:", error);
      queryClient.setQueryData(
        ["userAutoStacks"],
        (oldData: { results: UserAutoStackResponse[] }) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            results: oldData.results.map((stack: UserAutoStackResponse) =>
              stack._id === stackId
                ? { ...stack, status: currentStatus }
                : stack
            ),
          };
        }
      );
      await queryClient.refetchQueries({ queryKey: ["userAutoStacks"] });
    } finally {
      setUpdatingStackId(null);
    }
  };

  const formatUserStackData = (userStack: any): StackData => {
    const actualTokens = currencyFormatter.format(
      userStack.asset_balance || 0,
      "crypto",
      userStack.asset?.asset
    );

    const currentValue = currencyFormatter.format(
      userStack.fiat_balance || 0,
      "fiat",
      "ZAR"
    );

    const amountText = currencyFormatter.format(
      userStack.amount || 0,
      "fiat",
      "ZAR"
    );

    return {
      id: userStack._id,
      icon: userStack.asset?.image,
      assetName: userStack.asset.assetName,
      nextPurchase: userStack.next_purchase?.scheduled_purchase_date || "",
      title: userStack.name || userStack.asset?.assetName,
      amountText,
      frequency: userStack.frequency,
      amount: userStack.amount,
      estimatedTokens: `${actualTokens} ${userStack.asset?.asset.toUpperCase()}`,
      estimatedValue: currentValue,
      isUserStack: true,
      stackData: userStack,
    };
  };

  const hasUserStacks =
    !isLoadingUserStacks &&
    !errorUserStacks &&
    userAutoStacks?.results &&
    userAutoStacks.results.length > 0;

  const shouldShowUserStacks = hasUserStacks;
  const shouldShowTemplates = !isLoadingUserStacks && !hasUserStacks;

  const isDataLoaded = shouldShowUserStacks
    ? !isLoadingUserStacks
    : shouldShowTemplates &&
      !isLoadingTemplates &&
      !isFetchingTemplates &&
      !isFetchingUserStacks;
  const sectionTitle = isDataLoaded
    ? shouldShowUserStacks
      ? "My Stacks"
      : "Pre-Made Stacks For You"
    : "";

  const stackData: StackData[] = shouldShowUserStacks
    ? userAutoStacks?.results?.map((userStack: UserAutoStackResponse) =>
        formatUserStackData(userStack)
      ) || []
    : shouldShowTemplates
      ? autoStackTemplates?.results?.map(
          (template: AutoStackingTemplateResponse & { asset: Asset }) => {
            const estimatedTokens = currencyFormatter.format(
              template.estimated12MonthAccumulation?.[template.asset.asset],
              "crypto",
              template.asset.asset
            );

            const estimatedValue = currencyFormatter.format(
              template.estimated12MonthAccumulation?.zar,
              "fiat",
              "ZAR"
            );

            const amountText = currencyFormatter.format(
              template.amount,
              "fiat",
              "ZAR"
            );

            return {
              id: template.name,
              icon: template.asset.image,
              assetName: template.asset.assetName,
              title: template.name,
              amountText,
              frequency: template.frequency,
              amount: template.amount,
              estimatedTokens: `${estimatedTokens} ${template.asset.asset.toUpperCase()}`,
              estimatedValue,
              isUserStack: false,
            };
          }
        ) || []
      : [];

  return (
    <>
      <Header />
      <div className="px-6 md:px-14 py-6 bg-bg-light-gray rounded-t-2xl min-h-[calc(100vh-100px)]">
        <div className="flex items-center flex-wrap md:flex-nowrap gap-4 w-full mb-8">
          <div
            className="h-80 autostack-header-card flex flex-col justify-between p-6 rounded-lg w-full"
            style={{
              background:
                "radial-gradient(circle at top right, #E91E63 0%, #3B4BC7 70%)",
            }}
          >
            <Typography
              variant="h1"
              className="text-2xl font-semibold text-white"
            >
              AutoStack
            </Typography>
            <div className="flex flex-col gap-1">
              <Typography variant="p" className="text-bg-neutral text-sm">
                Total stacked
              </Typography>
              <Typography
                variant="h1"
                className="text-white text-4xl font-semibold"
              >
                {formatTotalStacked()}
              </Typography>
            </div>
          </div>
          <div className="w-full">
            {isLoadingUserStacks ||
            isLoadingDigitalWallet ||
            isFetchingDigitalWallet ? (
              <AutoStackPerformanceChartSkeleton />
            ) : shouldShowUserStacks ? (
              <AutoStackPerformanceChart
                autoStackStats={autoStackStats}
                isLoading={isLoadingAutoStackStats || isFetchingAutoStackStats}
              />
            ) : (
              <AutoStackCarousel />
            )}
          </div>
        </div>
        {sectionTitle && (
          <div className="flex items-center justify-between flex-wrap gap-4 w-full mb-4">
            <Typography
              variant="h2"
              className="text-xl text-text-primary font-semibold"
            >
              {sectionTitle}
            </Typography>
            <div className="flex items-center gap-2">
              <Button
                className="btn-primary !p-6"
                onClick={() => navigate("/autostack/create")}
              >
                <PlusIcon className="w-4 h-4" />
                <Typography variant="p" className="text-sm text-white">
                  Create new stack
                </Typography>
              </Button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {isLoadingUserStacks ||
          isFetchingUserStacks ||
          isLoadingDigitalWallet ||
          isFetchingDigitalWallet ||
          (shouldShowTemplates &&
            (isLoadingTemplates ||
              isFetchingTemplates ||
              isLoadingUserStacks ||
              isFetchingUserStacks)) ? (
            Array.from({ length: 3 }).map((_, index) => (
              <MyStacksSkeleton key={index} />
            ))
          ) : stackData.length === 0 ? (
            <div className="col-span-full flex items-center justify-center py-8">
              <Typography variant="p" className="text-gray-500">
                {shouldShowUserStacks
                  ? "No stacks created yet"
                  : "No templates available"}
              </Typography>
            </div>
          ) : (
            stackData.map((stack: StackData, index: number) => {
              const template = shouldShowUserStacks
                ? null
                : autoStackTemplates?.results?.at(index);
              const userStack = shouldShowUserStacks ? stack.stackData : null;
              return (
                <StackCard
                  key={stack.id}
                  id={stack.id}
                  icon={stack.icon}
                  title={stack.title}
                  assetName={stack.assetName}
                  nextPurchase={stack.nextPurchase}
                  amountText={stack.amountText}
                  frequency={stack.frequency}
                  amount={stack.amount}
                  estimatedTokens={stack.estimatedTokens}
                  estimatedValue={stack.estimatedValue}
                  isUserStack={stack.isUserStack}
                  status={userStack?.status}
                  fiatWalletBalance={fiatWalletBalance}
                  onToggleStatus={
                    stack.isUserStack && userStack
                      ? () =>
                          handleToggleStackStatus(stack.id, userStack.status)
                      : undefined
                  }
                  isUpdating={updatingStackId === stack.id}
                  isStatusChanging={updatingStackId === stack.id}
                  onViewPerformance={
                    stack.isUserStack
                      ? () => navigate(`/autostack/${stack.id}`)
                      : undefined
                  }
                  onCreateStack={() => {
                    if (stack.isUserStack) {
                      navigate(`/autostack/stackmore/${stack.id}`);
                    } else {
                      navigate("/autostack/create", {
                        state: {
                          prefillData: template,
                        },
                      });
                    }
                  }}
                />
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
